import { useState, useEffect, useRef, useCallback } from "react";
import { Sparkles, RefreshCw, Target, Crown, Flame, Scale, GitCompareArrows, CheckCircle2, Circle, SlidersHorizontal, CalendarDays, Check, Moon } from "lucide-react";
import { useWellnessState } from "@/context/WellnessStateContext";
import { usePeriod } from "@/context/PeriodContext";
import { glowStandards, bodyModes, computeWellnessMetrics, periodLabel, iso, uid, focusStreak, focusWeek } from "@/lib/wellness";
import { wellnessApi, syncApi } from "@/lib/api";
import { PeriodNav } from "@/components/PeriodNav";
import { PageHeader, SectionCard, KpiCard, EmptyState } from "@/components/shared";
import { FormModal, SelectField, TextField } from "@/components/forms";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

const Ring = ({ value }) => {
  const r = 52, c = 2 * Math.PI * r, off = c - (value / 100) * c;
  return (
    <div className="relative h-[136px] w-[136px]">
      <svg viewBox="0 0 132 132" className="h-full w-full -rotate-90">
        <circle cx="66" cy="66" r={r} fill="none" stroke="rgba(23,35,31,0.08)" strokeWidth="12" />
        <circle cx="66" cy="66" r={r} fill="none" stroke="var(--green)" strokeWidth="12" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={off} className="transition-all duration-500" />
      </svg>
      <div className="absolute inset-0 grid place-items-center"><div className="text-center"><div className="font-serif-display text-3xl text-[var(--text)] tabular-nums" data-testid="wellness-readiness">{value}</div><div className="text-[10px] uppercase tracking-wide text-[var(--text-muted)]">points</div></div></div>
    </div>
  );
};

const tagTone = (tag) => {
  const t = (tag || "").toLowerCase();
  if (["faith", "mindset", "presence", "rest", "recovery"].includes(t)) return "gold";
  return "green";
};

const energyOpts = [{ value: "Low", label: "Low" }, { value: "Okay", label: "Okay" }, { value: "Good", label: "Good" }, { value: "Great", label: "Great" }];
const sleepOpts = [{ value: "Under 5h", label: "Under 5h" }, { value: "5-6h", label: "5–6 hours" }, { value: "7-8h", label: "7–8 hours" }, { value: "8h+", label: "8h+" }];
const timeOpts = [{ value: "15 min", label: "~15 min" }, { value: "30 min", label: "~30 min" }, { value: "1 hour", label: "~1 hour" }, { value: "Flexible", label: "Flexible" }];

export default function WellnessCommand() {
  const { state, update, loading, setState } = useWellnessState();
  const { horizon, setHorizon, date, setDate } = usePeriod();
  const [planLoading, setPlanLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [tuneOpen, setTuneOpen] = useState(false);
  const [checkin, setCheckin] = useState({ energy: "Good", sleep: "7-8h", time: "30 min", feeling: "" });
  const planRequested = useRef(null);
  const syncedOnce = useRef(false);
  const today = iso();
  const isToday = date === today;

  const runSync = useCallback(async (manual) => {
    setSyncing(true);
    try {
      const res = await syncApi.health();
      setState((prev) => ({ ...prev, customHabits: res.state.customHabits }));
      if (manual) {
        const parts = [];
        if (res.added) parts.push(`pulled ${res.added} from Life`);
        if (res.lifeUpdated) parts.push(`updated ${res.lifeUpdated} Life goal${res.lifeUpdated === 1 ? "" : "s"}`);
        toast.success(parts.length ? `Synced — ${parts.join(" · ")}` : "Both centers already in sync");
      }
    } catch { if (manual) toast.error("Couldn't sync from Life"); }
    finally { setSyncing(false); }
  }, [setState]);

  useEffect(() => { if (!state || syncedOnce.current) return; syncedOnce.current = true; runSync(false); }, [state, runSync]);

  const generatePlan = useCallback(async (checkinPayload) => {
    if (!state) return;
    setPlanLoading(true);
    try {
      const mm = computeWellnessMetrics(state, today);
      const res = await wellnessApi.dailyPlan({ date: today, readiness: mm.earned, planned: mm.planned, checkin: checkinPayload || null });
      const actions = (res.actions || []).slice(0, 3).map((a) => ({ id: uid("wa"), title: a.title, why: a.why, tag: a.tag, points: a.points, done: false }));
      update((prev) => ({
        ...prev,
        dailyPlans: { ...(prev.dailyPlans || {}), [today]: { headline: res.headline, summary: res.summary, affirmation: res.affirmation, actions, generatedAt: new Date().toISOString(), checkin: checkinPayload || null } },
      }), "Generated AI daily focus");
      toast.success("Your Top 3 for today are ready");
    } catch {
      toast.error("Couldn't build today's focus right now");
      planRequested.current = null;
    } finally {
      setPlanLoading(false);
    }
  }, [state, today, update]);

  // Auto-generate once per day (cached), only when viewing today.
  useEffect(() => {
    if (!state || !isToday) return;
    if (state.dailyPlans?.[today]) return;
    if (planRequested.current === today) return;
    planRequested.current = today;
    generatePlan(null);
  }, [state, isToday, today, generatePlan]);

  if (loading || !state) return <Skeleton className="h-96 rounded-[14px]" />;

  const m = computeWellnessMetrics(state, date);
  const plan = state.dailyPlans?.[today];
  const setMode = (mode) => update((prev) => ({ ...prev, bodyMode: mode }), "Updated body mode");
  const toggleAction = (id) => update((prev) => {
    const p = prev.dailyPlans?.[today];
    if (!p) return prev;
    const actions = p.actions.map((a) => (a.id === id ? { ...a, done: !a.done } : a));
    return { ...prev, dailyPlans: { ...prev.dailyPlans, [today]: { ...p, actions } } };
  }, "Updated focus action");
  const submitTune = () => { setTuneOpen(false); planRequested.current = today; generatePlan(checkin); };
  const onRestDay = () => { planRequested.current = today; generatePlan({ restDay: true, energy: "Low", time: "Flexible" }); };

  const planDone = plan ? plan.actions.filter((a) => a.done).length : 0;
  const planPts = plan ? plan.actions.reduce((s, a) => s + (a.done ? a.points : 0), 0) : 0;
  const streak = focusStreak(state, today);
  const week = focusWeek(state, today);
  const weekdayNarrow = (ds) => new Date(ds + "T12:00:00").toLocaleDateString("en-US", { weekday: "narrow" });

  return (
    <div>
      <PageHeader title="Command" subtitle="Your AI-guided Top 3 for today, readiness and radiant rhythms." testid="wellness-command-header"
        actions={<Button variant="outline" size="sm" onClick={() => runSync(true)} disabled={syncing} data-testid="sync-from-life-button" className="border-[color:var(--border-subtle)] hover:bg-[color:rgba(198,139,60,0.1)]"><GitCompareArrows className={cn("h-4 w-4 mr-1.5 text-[var(--gold)]", syncing && "animate-spin")} />Sync with Life</Button>} />

      <div className="mb-4"><PeriodNav horizon={horizon} setHorizon={setHorizon} date={date} setDate={setDate} testid="command-period" /></div>
      {horizon !== "Day" && <p className="-mt-2 mb-4 text-[12px] text-[var(--text-muted)]" data-testid="command-day-note">Tracking habits for {periodLabel("Day", date)} within this {horizon.toLowerCase()}.</p>}

      {/* AI Today's Focus — today only */}
      {isToday ? (
        <SectionCard className="mb-4" testid="ai-focus-card"
          title={<span className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-[var(--gold)]" />Today&apos;s Focus
            {streak > 0 && <span className="inline-flex items-center gap-1 rounded-full bg-[color:rgba(198,139,60,0.16)] px-2 py-0.5 text-[11px] font-medium text-[#8a5a17]" data-testid="focus-streak"><Flame className="h-3 w-3" />{streak}-day streak</span>}
          </span>}
          subtitle={plan ? `${planDone} of 3 done · +${planPts} pts earned` : "Your AI-chosen Top 3 for today"}
          right={<div className="flex items-center gap-1.5">
            <Button variant="ghost" size="sm" onClick={onRestDay} disabled={planLoading} data-testid="rest-day-button" className="text-[var(--text-muted)] h-8"><Moon className="h-3.5 w-3.5 mr-1" />Rest day</Button>
            <Button variant="ghost" size="sm" onClick={() => setTuneOpen(true)} data-testid="tune-today-button" className="text-[var(--text-muted)] h-8"><SlidersHorizontal className="h-3.5 w-3.5 mr-1" />Tune today</Button>
            <Button variant="ghost" size="sm" onClick={() => generatePlan(plan?.checkin || null)} disabled={planLoading} data-testid="regenerate-focus-button" className="text-[var(--text-muted)] h-8"><RefreshCw className={cn("h-3.5 w-3.5 mr-1", planLoading && "animate-spin")} />Regenerate</Button>
          </div>}>
          {planLoading && !plan ? (
            <div className="space-y-3" data-testid="focus-loading">
              <Skeleton className="h-5 w-56" /><Skeleton className="h-4 w-full" />
              <Skeleton className="h-16 w-full rounded-[12px]" /><Skeleton className="h-16 w-full rounded-[12px]" /><Skeleton className="h-16 w-full rounded-[12px]" />
            </div>
          ) : plan ? (
            <div>
              <h3 className="font-serif-display text-xl text-[var(--text)]" data-testid="focus-headline">{plan.headline}</h3>
              {plan.summary && <p className="text-sm text-[var(--text-muted)] mt-1 leading-relaxed" data-testid="focus-summary">{plan.summary}</p>}
              <div className="mt-3 space-y-2">
                {plan.actions.map((a, i) => {
                  const tone = tagTone(a.tag);
                  return (
                    <div key={a.id} className={cn("flex items-start gap-3 rounded-[12px] border px-3 py-3 transition-colors", a.done ? "border-[color:rgba(31,115,85,0.4)] bg-[color:rgba(31,115,85,0.06)]" : "border-[color:var(--border-subtle)] bg-white hover:border-[color:var(--border-strong)]")} data-testid={`focus-action-${i}`}>
                      <button onClick={() => toggleAction(a.id)} aria-label="Toggle focus action" data-testid={`focus-toggle-${i}`} className="mt-0.5 transition-transform active:scale-90">
                        {a.done ? <CheckCircle2 className="h-5 w-5 text-[var(--green)]" /> : <Circle className="h-5 w-5 text-[var(--text-faint)]" />}
                      </button>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={cn("text-sm font-medium", a.done ? "line-through text-[var(--text-faint)]" : "text-[var(--text)]")}>{a.title}</span>
                          <span className={cn("inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium", tone === "gold" ? "bg-[color:rgba(198,139,60,0.16)] text-[#8a5a17]" : "bg-[color:rgba(31,115,85,0.12)] text-[var(--green)]")}>{a.tag}</span>
                        </div>
                        {a.why && <p className="text-[12px] text-[var(--text-muted)] mt-0.5">{a.why}</p>}
                      </div>
                      <span className="text-[11px] tabular-nums text-[var(--green)] font-medium shrink-0 mt-0.5">+{a.points}</span>
                    </div>
                  );
                })}
              </div>
              {plan.affirmation && <p className="text-[13px] text-[var(--gold)] mt-3 font-medium" data-testid="focus-affirmation">{plan.affirmation}</p>}
              <div className="mt-3 pt-3 border-t border-[color:var(--border-subtle)]" data-testid="focus-history">
                <div className="text-[11px] uppercase tracking-[0.14em] text-[var(--text-muted)] mb-2">This week&apos;s focus</div>
                <div className="flex items-center gap-2">
                  {week.map((d) => {
                    const complete = d.total > 0 && d.done === d.total;
                    const partial = d.done > 0 && !complete;
                    const isTodayCell = d.date === today;
                    return (
                      <div key={d.date} className="flex flex-col items-center gap-1" data-testid={`focus-week-${d.date}`}>
                        <span className="text-[10px] text-[var(--text-faint)]">{weekdayNarrow(d.date)}</span>
                        <div title={`${d.done}/${d.total || 3}`} className={cn("h-7 w-7 rounded-[8px] grid place-items-center text-[11px] tabular-nums border transition-colors",
                          complete ? "bg-[var(--green)] text-white border-transparent" : partial ? "bg-[color:rgba(198,139,60,0.16)] text-[#8a5a17] border-[color:rgba(198,139,60,0.3)]" : "border-[color:var(--border-subtle)] text-[var(--text-faint)]",
                          isTodayCell && "ring-2 ring-offset-1 ring-[color:rgba(198,139,60,0.5)]")}>
                          {complete ? <Check className="h-3.5 w-3.5" /> : (d.total ? d.done : "·")}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          ) : (
            <EmptyState icon={Sparkles} title="Let's set your Top 3" message="Generate an AI focus for today, tuned to your body, life and season." action={<Button onClick={() => generatePlan(null)} data-testid="generate-focus-button" className="bg-[var(--green)] text-[#f7fbf9] hover:bg-[color:rgba(31,115,85,0.9)]"><Sparkles className="h-4 w-4 mr-1.5" />Generate today&apos;s focus</Button>} />
          )}
        </SectionCard>
      ) : (
        <div className="mb-4 rounded-[var(--radius-card)] border border-[color:var(--border-subtle)] bg-[var(--surface-1)] p-4 flex items-center justify-between gap-3" data-testid="focus-not-today">
          <div className="flex items-center gap-2 text-sm text-[var(--text-muted)]"><CalendarDays className="h-4 w-4 text-[var(--gold)]" />Your AI Top 3 focus is set for today. You&apos;re viewing {periodLabel("Day", date)}.</div>
          <Button variant="outline" size="sm" onClick={() => { setHorizon("Day"); setDate(today); }} data-testid="jump-to-today-button" className="border-[color:var(--border-subtle)]">Go to today</Button>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <KpiCard label="Readiness" value={`${m.earned}/${m.planned}`} sub="Points today" icon={Flame} accent testid="kpi-w-readiness" />
        <KpiCard label="Goal progress" value={`${m.goalAvg}%`} sub="Average across goals" icon={Target} testid="kpi-w-goals" />
        <KpiCard label="Glow ritual" value={`${m.glowDone}/${glowStandards.length}`} sub="Standards today" icon={Crown} testid="kpi-w-glow" />
        <KpiCard label="Latest weight" value={m.latest?.weight ? `${m.latest.weight} lb` : "—"} sub={m.latest ? `Energy ${m.latest.energy}/10` : "Log a check-in"} icon={Scale} testid="kpi-w-weight" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <SectionCard testid="wellness-ring-card" className="lg:col-span-5">
          <div className="flex items-center gap-5">
            <Ring value={Math.min(100, m.earned)} />
            <div className="min-w-0">
              <div className="text-[11px] uppercase tracking-[0.14em] text-[var(--text-muted)]">Today's readiness</div>
              <p className="font-serif-display text-lg text-[var(--text)] mt-1 leading-snug">{m.earned >= 80 ? "Radiant day — finish with ease." : m.earned >= 40 ? "Beautiful momentum. Keep going." : "One kind choice starts it all."}</p>
              <p className="text-sm text-[var(--text-muted)] mt-1">{m.earned} of {m.planned} points earned</p>
            </div>
          </div>
        </SectionCard>
        <SectionCard title="Body mode" subtitle="Set the intention for this season" testid="body-mode-card" className="lg:col-span-7">
          <div className="grid grid-cols-3 gap-2">
            {bodyModes.map((mode) => (
              <button key={mode} onClick={() => setMode(mode)} data-testid={`mode-${mode}`}
                className={cn("rounded-[10px] border px-2 py-2.5 text-sm capitalize transition-colors", state.bodyMode === mode ? "border-[color:rgba(31,115,85,0.5)] bg-[color:rgba(31,115,85,0.08)] text-[var(--text)] font-medium" : "border-[color:var(--border-subtle)] text-[var(--text-muted)] hover:text-[var(--text)]")}>{mode}</button>
            ))}
          </div>
        </SectionCard>
      </div>

      <FormModal open={tuneOpen} onOpenChange={setTuneOpen} title="How are you today?" description="Optional — a few taps help the AI tailor your Top 3 for today." onSubmit={submitTune} submitLabel="Rebuild my Top 3" testid="tune-modal">
        <SelectField label="Energy right now" value={checkin.energy} onChange={(v) => setCheckin({ ...checkin, energy: v })} options={energyOpts} testid="tune-energy" />
        <SelectField label="Last night's sleep" value={checkin.sleep} onChange={(v) => setCheckin({ ...checkin, sleep: v })} options={sleepOpts} testid="tune-sleep" />
        <SelectField label="Time available" value={checkin.time} onChange={(v) => setCheckin({ ...checkin, time: v })} options={timeOpts} testid="tune-time" />
        <TextField label="How you feel (optional)" value={checkin.feeling} onChange={(e) => setCheckin({ ...checkin, feeling: e.target.value })} placeholder="e.g. sore, motivated, stressed" testid="tune-feeling" />
      </FormModal>
    </div>
  );
}
